import { TreeItem } from '@siemens/element-ng/tree-view';
import { CheckboxTreeNode } from './checkbox-tree-node.type';
import { CheckboxTreeViewSelectionCategory } from './checkbox-tree-view-selection-category.interface';

export class CheckboxTreeOperations {
    public static build<T>(
        items: T[],
        itemLabel: (item: T) => string,
        itemCategory: (item: T) => CheckboxTreeViewSelectionCategory | null
    ): TreeItem<CheckboxTreeNode<T>>[] {
        const uncategorized: TreeItem<CheckboxTreeNode<T>>[] = [];
        const categories = new Map<string | number, TreeItem<CheckboxTreeNode<T>>>();
        const labelCollator = new Intl.Collator(undefined, { sensitivity: 'base' });
        const compareTreeItems = (left: TreeItem<CheckboxTreeNode<T>>, right: TreeItem<CheckboxTreeNode<T>>): number =>
            labelCollator.compare(left.label ?? '', right.label ?? '');

        for (const item of items) {
            const leaf: TreeItem<CheckboxTreeNode<T>> = {
                label: itemLabel(item),
                state: 'leaf',
                selectable: true,
                showCheckbox: true,
                checked: 'unchecked',
                customData: { kind: 'item', item }
            };
            const category = itemCategory(item);
            if (!category) {
                uncategorized.push(leaf);
                continue;
            }

            let categoryNode = categories.get(category.id);
            if (!categoryNode) {
                categoryNode = {
                    label: category.name,
                    state: 'expanded',
                    selectable: true,
                    showCheckbox: false,
                    customData: { kind: 'category', categoryId: category.id, selectedCount: 0, totalCount: 0 },
                    children: []
                };
                categories.set(category.id, categoryNode);
            }
            categoryNode.children ??= [];
            categoryNode.children.push(leaf);
            if (categoryNode.customData?.kind === 'category') categoryNode.customData.totalCount++;
        }

        const sortedCategories = [...categories.values()].sort(compareTreeItems);
        for (const category of sortedCategories) category.children?.sort(compareTreeItems);

        return [...uncategorized.sort(compareTreeItems), ...sortedCategories];
    }

    public static syncSelection<T>(
        treeItems: TreeItem<CheckboxTreeNode<T>>[],
        selectedIds: Set<string | number>,
        itemId: (item: T) => string | number
    ): void {
        for (const treeItem of treeItems) {
            const node = treeItem.customData;
            if (node?.kind === 'category') {
                node.selectedCount = 0;
                for (const child of treeItem.children ?? []) {
                    const childNode = child.customData;
                    if (childNode?.kind !== 'item') continue;
                    const checked = selectedIds.has(itemId(childNode.item));
                    child.checked = checked ? 'checked' : 'unchecked';
                    if (checked) node.selectedCount++;
                }
            } else if (node?.kind === 'item') {
                treeItem.checked = selectedIds.has(itemId(node.item)) ? 'checked' : 'unchecked';
            }
        }
    }
}
